import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogIn, ChevronDown } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import orthodoxLogo from "../../assets/orthodox-church-logo.png";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [mobileDropdowns, setMobileDropdowns] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const navigation = [
    {
      name: "Home",
      href: "/",
      submenu: [
        { name: "Welcome Message", href: "/#welcome-message" },
        { name: "Bible Verse of the Day", href: "/#daily-verse" },
        {
          name: "Daily Desert Monastic Wisdom",
          href: "/#daily-monastic-wisdom",
        },
        { name: "Service Times", href: "/#service-times" },
        { name: "Upcoming Events", href: "/#upcoming-events" },
        { name: "News Bulletin", href: "/#news-bulletin" },
      ],
    },
    {
      name: "Services",
      href: "/services",
      submenu: [
        { name: "Sunday Service", href: "/services#sunday-service" },
        { name: "Special Day", href: "/services#special-day" },
        { name: "Lectionary", href: "/services#lectionary" },
        { name: "Resources", href: "/services#resources" },
      ],
    },
    {
      name: "Events & News",
      href: "/eventsandnews",
      submenu: [
        { name: "Upcoming Events", href: "/eventsandnews#upcoming-events" },
        { name: "News Bulletin", href: "/eventsandnews#news-bulletin" },
      ],
    },
    {
      name: "Organizations",
      href: "/organizations",
      submenu: [
        { name: "Sunday School", href: "/organizations#sunday-school" },
        { name: "MMVS", href: "/organizations#mmvs" },
        { name: "MGOCSM", href: "/organizations#mgocsm" },
        { name: "Prayer Group", href: "/organizations#prayer-group" },
      ],
    },
    { name: "Gallery", href: "/gallery" },
    { name: "Prayer Request", href: "/prayer-request" },
    {
      name: "About",
      href: "/about",
      submenu: [
        { name: "Our History", href: "/about#our-history" },
        { name: "Our Clergy", href: "/about#our-clergy" },
        { name: "Former Vicars", href: "/about#former-vicars" },
        { name: "Managing Committee", href: "/about#managing-committee" },
        { name: "Visit us", href: "/about#visit-us" },
      ],
    },
    { name: "Donate", href: "/donate" },
  ];

  const handleAdminClick = () => {
    navigate("/admin/dashboard");
  };

  const handleLoginClick = () => {
    navigate("/admin/login");
  };

  const handleMouseEnter = (itemName) => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setActiveDropdown(itemName);
  };

  const handleMouseLeave = () => {
    // Set a timeout to close the dropdown after a delay
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
    setHoverTimeout(timeout);
  };

  const toggleMobileDropdown = (itemName) => {
    setMobileDropdowns((prev) => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  const scrollToSection = (href) => {
    if (href.includes("#")) {
      const [path, section] = href.split("#");
      const currentPath = location.pathname;
      const currentSearch = location.search;

      // Check if we're on the same path (ignoring query parameters like ?resource_folder=1)
      const isSamePath = path === "" || path === currentPath;

      if (isSamePath && !currentSearch.includes("resource_folder")) {
        // Same page and not in a folder, just scroll
        const element = document.getElementById(section);
        if (element) {
          const headerHeight =
            document.querySelector("header")?.offsetHeight || 0;
          const elementPosition = element.offsetTop - headerHeight - 20;
          window.scrollTo({
            top: elementPosition,
            behavior: "smooth",
          });
        }
      } else {
        // Different page OR in a resource folder, navigate to clean path with section
        navigate(path, { state: { scrollToSection: section } });
      }
    } else {
      navigate(href);
    }
    setActiveDropdown(null);
    setIsMenuOpen(false);
    setMobileDropdowns({});
  };

  // Handle scrolling when page changes and has a section to scroll to
  useEffect(() => {
    if (location.state?.scrollToSection) {
      // Wait for the page to render, then scroll
      const timer = setTimeout(() => {
        const element = document.getElementById(location.state.scrollToSection);
        if (element) {
          const headerHeight =
            document.querySelector("header")?.offsetHeight || 0;
          const elementPosition = element.offsetTop - headerHeight - 20;
          window.scrollTo({
            top: elementPosition,
            behavior: "smooth",
          });
        }
      }, 300);

      // Clear the scroll state to prevent scrolling on subsequent renders
      window.history.replaceState({}, "");

      return () => clearTimeout(timer);
    }
  }, [location]);

  // Handle direct hash navigation (when someone visits a URL with #section)
  useEffect(() => {
    if (location.hash) {
      const timer = setTimeout(() => {
        const element = document.getElementById(location.hash.substring(1));
        if (element) {
          const headerHeight =
            document.querySelector("header")?.offsetHeight || 0;
          const elementPosition = element.offsetTop - headerHeight - 20;
          window.scrollTo({
            top: elementPosition,
            behavior: "smooth",
          });
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="w-full px-2 sm:px-4 lg:px-6">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-12 flex items-center justify-center">
                <img
                  src={orthodoxLogo}
                  alt="Orthodox Church Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="lg:block">
                <h1 className="text-sm font-bold text-gray-900">
                  St. Mary's Indian Orthodox Church
                </h1>
                <p className="text-xs text-gray-600">San Antonio, Texas</p>
              </div>
            </Link>
          </div>

          <div className="flex items-center space-x-2">
            <nav className="hidden md:flex items-center space-x-2">
              {navigation.map((item, index) => (
                <div key={item.name} className="flex items-center">
                  {index > 0 && (
                    <div className="w-px h-6 bg-gray-300 mr-4"></div>
                  )}
                  <div
                    className="relative"
                    onMouseEnter={() =>
                      item.submenu && handleMouseEnter(item.name)
                    }
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="flex items-center">
                      <Link
                        to={item.href}
                        className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                          location.pathname === item.href
                            ? "text-red-600 border-b-2 border-red-600"
                            : "text-gray-700 hover:text-red-600"
                        }`}
                      >
                        {item.name}
                      </Link>
                      {item.submenu && (
                        <ChevronDown className="w-4 h-4 ml-1 text-gray-500" />
                      )}
                    </div>

                    {item.submenu && activeDropdown === item.name && (
                      <div
                        className="absolute top-full left-0 w-48 bg-white rounded-md shadow-lg py-1 z-50 border"
                        onMouseEnter={() => handleMouseEnter(item.name)}
                        onMouseLeave={handleMouseLeave}
                      >
                        {item.submenu.map((subItem) => (
                          <button
                            key={subItem.name}
                            onClick={() => scrollToSection(subItem.href)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-red-600 w-full text-left transition-colors duration-200"
                          >
                            {subItem.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </nav>

            {/* Admin/Login Section */}
            <div className="hidden md:flex items-center">
              <div className="w-px h-6 bg-gray-300 mr-4"></div>
              {user ? (
                <button
                  onClick={handleAdminClick}
                  className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-red-600 transition-colors duration-200"
                >
                  <User className="w-4 h-4" />
                  <span>{user.name}</span>
                </button>
              ) : (
                <button
                  onClick={handleLoginClick}
                  className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-red-600 transition-colors duration-200"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Login</span>
                </button>
              )}
            </div>
          </div>

          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-red-600 focus:outline-none"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col space-y-2">
              {navigation.map((item) => (
                <div key={item.name} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Link
                      to={item.href}
                      className={`text-base font-medium transition-colors duration-200 block ${
                        location.pathname === item.href
                          ? "text-red-600"
                          : "text-gray-700 hover:text-red-600"
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                    {item.submenu && (
                      <button
                        onClick={() => toggleMobileDropdown(item.name)}
                        className="p-2 text-gray-500 hover:text-red-600 transition-colors duration-200"
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${
                            mobileDropdowns[item.name] ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    )}
                  </div>
                  {item.submenu && mobileDropdowns[item.name] && (
                    <div className="ml-4 space-y-2 bg-gray-50 p-2 rounded-md">
                      {item.submenu.map((subItem) => (
                        <button
                          key={subItem.name}
                          onClick={() => scrollToSection(subItem.href)}
                          className="block text-sm text-gray-600 hover:text-red-600 transition-colors duration-200 w-full text-left py-1"
                        >
                          {subItem.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Mobile Admin/Login Section */}
              <div className="border-t border-gray-200 pt-4">
                {user ? (
                  <button
                    onClick={() => {
                      handleAdminClick();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 text-base font-medium text-gray-700 hover:text-red-600 transition-colors duration-200"
                  >
                    <User className="w-4 h-4" />
                    <span>{user.name}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      handleLoginClick();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 text-base font-medium text-gray-700 hover:text-red-600 transition-colors duration-200"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Login</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
